import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { sessionStorage } from '@shared/helpers/storage'
import catQuestionTranslations from '@shared/modules/assessment/config/cat-questions.json'

const summaries = {
  // Please NOTE: for the CAT questionnaire, a lower score is better.
  // This is different in comparison to other questionnaires where higher score is better.
  low: {
    min: 0,
    max: 10,
    group: 'low',
    title: 'b2b_quiz_cat_low_title',
    recommendations: [
      {
        title: 'b2b_quiz_cat_profile_title',
        recommendation: 'b2b_quiz_cat_low_profile',
      },
      {
        title: 'b2b_quiz_cat_training_title',
        recommendation: 'b2b_quiz_cat_low_training',
      },
    ],
  },
  medium: {
    min: 11,
    max: 20,
    group: 'medium',
    title: 'b2b_quiz_cat_medium_title',
    recommendations: [
      {
        title: 'b2b_quiz_cat_profile_title',
        recommendation: 'b2b_quiz_cat_medium_profile',
      },
      {
        title: 'b2b_quiz_cat_training_title',
        recommendation: 'b2b_quiz_cat_medium_training',
      },
    ],
  },
  high: {
    min: 21,
    max: 30,
    group: 'high',
    title: 'b2b_quiz_cat_high_title',
    recommendations: [
      {
        title: 'b2b_quiz_cat_profile_title',
        recommendation: 'b2b_quiz_cat_high_profile',
      },
      {
        title: 'b2b_quiz_cat_training_title',
        recommendation: 'b2b_quiz_cat_high_training',
      },
    ],
  },
  veryhigh: {
    min: 31,
    max: 40,
    group: 'very-high',
    title: 'b2b_quiz_cat_very_high_title',
    recommendations: [
      {
        title: 'b2b_quiz_cat_profile_title',
        recommendation: 'b2b_quiz_cat_very_high_profile',
      },
      {
        title: 'b2b_quiz_cat_training_title',
        recommendation: 'b2b_quiz_cat_very_high_training',
      },
    ],
  },
}

export const useCATStore = defineStore(
  'cat',
  () => {
    // State
    const answers = ref({
      cough_frequency: null,
      phlegm_on_chest: null,
      chest_tightness: null,
      out_of_breath_when_walking: null,
      limited_activities_at_home: null,
      not_confident_leaving_home: null,
      bad_sleep: null,
      low_energy_level: null,
    })

    const score = ref(0) // Int [0 to 40]

    // Getters
    const catQuestions = computed(() =>
      Object.entries(answers.value).map(([key, value]) => ({
        key,
        value,
        ...catQuestionTranslations[key],
      })),
    )

    const catQuestionsScore = computed(() => score.value)

    const catSummary = computed(() => {
      const currentScore = score.value
      const key =
        currentScore > 30
          ? 'veryhigh'
          : currentScore > 20
            ? 'high'
            : currentScore > 10
              ? 'medium'
              : 'low'
      return {
        score: currentScore,
        summary: summaries[key],
      }
    })

    const catAnswers = computed(() =>
      Object.entries(answers.value).reduce((data, [key, value]) => {
        data[key.replace(/_/g, '-')] = value
        return data
      }, {}),
    )

    const updateCATScore = () => {
      score.value = Object.keys(answers.value).reduce(
        (sum, key) => sum + (answers.value[key] ?? 0),
        0,
      )
    }

    return {
      // State
      answers,
      score,
      // Getters
      catQuestions,
      catQuestionsScore,
      catSummary,
      catAnswers,
      updateCATScore,
    }
  },
  {
    persist: {
      storage: sessionStorage,
    },
  },
)

export default useCATStore
